import React from "react"
import { components } from "react-select"
type SelectComponentsProps = { [key: string]: any }
import classNames from 'classnames'

import IconClearIndicator from "assets/img/icon-clear-indicator.svg?react"
import IconDropdownIndicator from "assets/img/icon-dropdown-indicator.svg?react"
import IconRemoveTagWhite from "assets/img/icon-remove-tag-white.svg?react"

import SelectLoadingMessage from "./SelectLoadingMessage"
import SelectNoOptionsMessage from "./SelectNoOptionsMessage"
import SelectErrorMessage from "./SelectErrorMessage"
import { INPUT_MAX_LENGTH_DEFAULT } from "config/constants"
import SelectSearchArticleNotFound from "./SelectSearchArticleNotFound"
import i18n from "intl/i18n"

export interface INoOptionsMessageConfigJson {
    isError: boolean,
    message: string
    type?: "articleSearch"
}

const SelectComponents = ({
    dropdownIndicator = false,
    onCreateArticleClick,
    testIdPrefix = "select",
    markSelectedOption = true,
}: {
    dropdownIndicator?: boolean
    onCreateArticleClick?(): void
    testIdPrefix?: string
    markSelectedOption?: boolean
}): SelectComponentsProps => {
    function getTestId(testIdPostfix: string) {
        return `${testIdPrefix}-${testIdPostfix}`
    }

    const selectComponents: SelectComponentsProps = {
        ValueContainer: ({ children, ...props }) => {
            const items =
                React.Children.map(children, child => {
                    if (child && (props.isMulti ? child.type !== components.Placeholder : true)) {
                        return child?.props?.data?.partiallyChecked ? React.cloneElement(child, { className: 'select-value_partial' }) : child
                        //additional className for showing partiallyCheckedOptions
                    }
                    return null
                })

            return (
                <components.ValueContainer {...props as any}>
                    {items}
                    {props.isMulti && !props.selectProps.inputValue &&
                        <components.Placeholder {...props as any} isFocused={props.isFocused}>
                            {props.selectProps.placeholder}
                        </components.Placeholder>
                    }
                </components.ValueContainer>
            )
        },
        Option: props => {
            const existingEntriesItem = props.options.find(o => o.label === i18n.t('selfPopulating.existingEntries'))
            const groupHeadingProps = {
                clearValue: props.clearValue,
                cx: props.cx,
                getStyles: props.getStyles,
                getClassNames: props.getClassNames,
                id: getTestId("existing-entries-heading"),
                getValue: props.getValue,
                hasValue: props.hasValue,
                isMulti: props.isMulti,
                isRtl: props.isRtl,
                options: props.options,
                selectOption: props.selectOption,
                selectProps: props.selectProps,
                setValue: props.setValue,
                theme: props.theme,
                data: { label: i18n.t('selfPopulating.existingEntries'), options: [] },
                children: i18n.t('selfPopulating.existingEntries'),
            }
            const { onMouseMove, onMouseOver, ...restInnerProps } = props.innerProps;
            return (
                <>
                    <components.Option {...props} isSelected={!markSelectedOption ? false : props.isSelected} innerProps={restInnerProps} className={classNames(props.data.partiallyChecked && "checkbox-partially", props.data.borderTop && "border-top-grey-100")} />
                    {!!existingEntriesItem && existingEntriesItem.options.length === 0 &&
                        <>
                            <components.GroupHeading {...groupHeadingProps} />
                            <SelectNoOptionsMessage>{i18n.t('common.noResultsFound')}</SelectNoOptionsMessage>
                        </>
                    }
                </>
            )
        },
        GroupHeading: props => {
            if (!props.children) return null
            return <components.GroupHeading {...props} />
        },
        Input: props => (
            <components.Input
                {...props}
                maxLength={INPUT_MAX_LENGTH_DEFAULT}
                data-testid={getTestId("input")} />
        ),
        ClearIndicator: props => {
            const {
                getStyles,
                innerProps: { ref, ...restInnerProps },
            } = props
            return (
                <div
                    {...restInnerProps}
                    ref={ref}
                    style={getStyles("clearIndicator", props)}
                    data-testid={getTestId("clear")}>
                    <IconClearIndicator />
                </div>
            )
        },
        DropdownIndicator: dropdownIndicator ? (props) => {
            const {
                getStyles,
                innerProps: { ref, ...restInnerProps }
            } = props
            return (
                <div
                    {...restInnerProps}
                    ref={ref}
                    style={getStyles("dropdownIndicator", props)}>
                    <IconDropdownIndicator width="100%" height="100%" />
                </div>
            )
        } : null,
        MultiValueRemove: props => {
            return (
                <button {...props.innerProps}>
                    <IconRemoveTagWhite />
                </button>
            )
        },
        LoadingIndicator: null,
        IndicatorSeparator: null,
        LoadingMessage: SelectLoadingMessage,
        NoOptionsMessage: (props) => {
            try {
                const { isError, message, type }: INoOptionsMessageConfigJson = JSON.parse(props.children)
                if (isError) {
                    return <SelectErrorMessage>{message}</SelectErrorMessage>
                }
                if (type === "articleSearch" && onCreateArticleClick) {
                    return <SelectSearchArticleNotFound onCreateArticleClick={onCreateArticleClick} />
                }
                return <SelectNoOptionsMessage>{message}</SelectNoOptionsMessage>
            } catch (e) {
                return <SelectNoOptionsMessage>{i18n.t('common.noResultsFound')}</SelectNoOptionsMessage>
            }
        },
    }

    return selectComponents
}

export default SelectComponents
